import React, { useState, useEffect } from 'react';
import { Activity, Search, Filter, Clock, User, Eye, Loader2 } from 'lucide-react';
import apiService from '../../services/api';

const ActivitiesList = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getActivities();
      
      if (response.success && Array.isArray(response.data)) {
        setActivities(response.data);
      } else {
        setActivities([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Erreur lors de la récupération des activités:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE':
        return { icon: '➕', color: 'text-green-600 bg-green-100' };
      case 'UPDATE':
        return { icon: '✏️', color: 'text-blue-600 bg-blue-100' };
      case 'DELETE':
        return { icon: '🗑️', color: 'text-red-600 bg-red-100' };
      case 'VIEW':
        return { icon: '👁️', color: 'text-purple-600 bg-purple-100' };
      default:
        return { icon: '📋', color: 'text-gray-600 bg-gray-100' };
    }
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      'Patient': '👤',
      'Doctor': '👨‍⚕️',
      'Consultation': '🩺',
      'Hospital': '🏥',
      'HealthZone': '🗺️',
      'Disease': '🦠',
      'Study': '📊'
    };
    return icons[entityType] || '📄';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'À l\'instant';
    if (diffInSeconds < 3600) return `Il y a ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Il y a ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 604800) return `Il y a ${Math.floor(diffInSeconds / 86400)} j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const uniqueActions = [...new Set(activities.map(a => a.action))].sort();
  const uniqueEntityTypes = [...new Set(activities.map(a => a.entityType))].sort();

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = 
      activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.entityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.entityType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = !filterAction || activity.action === filterAction;
    const matchesEntityType = !filterEntityType || activity.entityType === filterEntityType;
    
    return matchesSearch && matchesAction && matchesEntityType;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Journal des Activités</h1>
          <p className="text-gray-600 mt-2">Historique des actions effectuées sur la plateforme</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
            <p className="text-gray-600">Chargement des activités...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative md:col-span-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher une activité..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les actions</option>
                  {uniqueActions.map(action => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <select
                  value={filterEntityType}
                  onChange={(e) => setFilterEntityType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tous les types</option>
                  {uniqueEntityTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={fetchActivities}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Actualiser
              </button>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              {filteredActivities.length} activité(s) trouvée(s)
            </div>
          </div>
        )}

        {/* Activities List */}
        {!loading && !error && (
          <div className="space-y-4">
            {filteredActivities.map((activity) => {
              const actionInfo = getActionIcon(activity.action);
              const entityIcon = getEntityIcon(activity.entityType);
              
              return (
                <div
                  key={activity.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start space-x-4">
                    {/* Action Icon */}
                    <div className={`p-3 rounded-lg ${actionInfo.color}`}>
                      <span className="text-2xl">{actionInfo.icon}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xl">{entityIcon}</span>
                            <span className="font-semibold text-gray-900 capitalize">
                              {activity.entityType}
                            </span>
                            {activity.entityName && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-600 truncate">{activity.entityName}</span>
                              </>
                            )}
                          </div>

                          {activity.description && (
                            <p className="text-sm text-gray-700 mb-2">{activity.description}</p>
                          )}

                          {activity.user && (
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <User className="h-4 w-4" />
                              <span className="font-medium">{activity.user.name}</span>
                              <span className="text-gray-400">•</span>
                              <span className="capitalize">{activity.user.role}</span>
                            </div>
                          )}

                          <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                            <div className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              <span>{formatDate(activity.createdAt)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="px-2 py-0.5 bg-gray-100 rounded font-medium">
                                {activity.action}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && filteredActivities.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune activité trouvée</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivitiesList;
